import { useState, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { providerApiClient } from '@/lib/api'
import { queueKeys } from '@/lib/queryKeys'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { VisitRow, ReviewVisitRow, PastVisitRow } from '@/components/visits/VisitRow'
import FollowUpRow from '@/components/visits/FollowUpRow'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'

function VisitTabContent<T extends { id: string }>({
  items,
  emptyMessage,
  renderItem,
}: {
  items: T[] | undefined
  emptyMessage: string
  renderItem: (item: T) => ReactNode
}) {
  if (items?.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">{emptyMessage}</p>
  }
  return <>{items?.map((item) => renderItem(item))}</>
}

export default function VisitQueuePage() {
  const [search, setSearch] = useState('')

  const { data: queue, isLoading, error, refetch } = useQuery({
    queryKey: queueKeys.list(),
    queryFn: () => providerApiClient.getQueue(),
    staleTime: 2 * 60 * 1000,
  })

  const filteredQueue = useMemo(() => {
    if (!queue) return null
    if (!search.trim()) return queue

    const term = search.toLowerCase()
    return {
      upcoming_video: queue.upcoming_video.filter((v) => v.patient_name.toLowerCase().includes(term)),
      awaiting_intake: queue.awaiting_intake.filter((v) => v.patient_name.toLowerCase().includes(term)),
      needs_review: queue.needs_review.filter((v) => v.patient_name.toLowerCase().includes(term)),
      past_visits: queue.past_visits.filter((v) => v.patient_name.toLowerCase().includes(term)),
      follow_ups_due: queue.follow_ups_due.filter((f) => f.patient_name.toLowerCase().includes(term)),
    }
  }, [queue, search])

  if (isLoading) {
    return <LoadingPage variant="list" />
  }

  if (error) {
    return <ErrorState message="Failed to load visits. Please try again." onRetry={() => refetch()} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Visits</h1>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search patients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search patients"
          className="pl-9"
        />
      </div>

      <Tabs defaultValue="review">
        <TabsList className="flex-wrap">
          <TabsTrigger value="review" className="gap-2">
            Needs Review
            {filteredQueue && filteredQueue.needs_review.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
                {filteredQueue.needs_review.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="gap-2">
            Upcoming
            {filteredQueue && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
                {filteredQueue.upcoming_video.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="awaiting" className="gap-2">
            Awaiting Intake
            {filteredQueue && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
                {filteredQueue.awaiting_intake.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="past" className="gap-2">
            Past
            {filteredQueue && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
                {filteredQueue.past_visits.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-2">
          <VisitTabContent
            items={filteredQueue?.upcoming_video}
            emptyMessage="No upcoming visits"
            renderItem={(visit) => <VisitRow key={visit.id} visit={visit} />}
          />
        </TabsContent>

        <TabsContent value="awaiting" className="space-y-2">
          <VisitTabContent
            items={filteredQueue?.awaiting_intake}
            emptyMessage="No visits awaiting intake"
            renderItem={(visit) => <VisitRow key={visit.id} visit={visit} />}
          />
        </TabsContent>

        <TabsContent value="review" className="space-y-4">
          <div className="space-y-2">
            <VisitTabContent
              items={filteredQueue?.needs_review}
              emptyMessage="No visits need review"
              renderItem={(visit) => <ReviewVisitRow key={visit.id} visit={visit} />}
            />
          </div>

          {filteredQueue && filteredQueue.follow_ups_due.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium text-slate-900">Follow-Ups Due</h2>
              <div className="space-y-2">
                {filteredQueue.follow_ups_due.map((followUp) => (
                  <FollowUpRow key={followUp.last_visit_id} followUp={followUp} />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="space-y-2">
          <VisitTabContent
            items={filteredQueue?.past_visits}
            emptyMessage="No past visits"
            renderItem={(visit) => <PastVisitRow key={visit.id} visit={visit} />}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
